import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useUserAuth } from "../context/UserAuthContext";
import { db } from '../config/fbConfig';
import {
  collection,
  getDocs,
  where,
  query,
  Timestamp,
  updateDoc,
  doc
} from 'firebase/firestore';
import Loader from "./Loader";
import SuccessModal from './SuccessModal';
import Modal from 'react-modal';
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import Logo from "../images/hotel_logo.png";
import { HOTEL_NAME } from "../Variables.js";

Modal.setAppElement('#root');

const HotelOperationsDashboard = () => {
  const { user, userData } = useUserAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [showModal, setShowModal] = useState(false);

  // Period selector
  const [selectedPeriod, setSelectedPeriod] = useState("thisMonth");

  // Room type modal states
  const [isRoomTypeModalOpen, setIsRoomTypeModalOpen] = useState(false);
  const [selectedRoomType, setSelectedRoomType] = useState(null); // { id, name }
  const [roomsInType, setRoomsInType] = useState([]);
  const [selectedRooms, setSelectedRooms] = useState([]);
  const [newStatus, setNewStatus] = useState("Available");

  // General room modal (existing)
  const [isRoomModalOpen, setIsRoomModalOpen] = useState(false);
  const [selectedRoomCategory, setSelectedRoomCategory] = useState("");

  const [reportBookings, setReportBookings] = useState([]);
  const [reportStartDate, setReportStartDate] = useState("");
  const [reportEndDate, setReportEndDate] = useState("");
  const [isReportModalOpen, setIsReportModalOpen] = useState(false); // Single report modal

  const [products, setProducts] = useState([]);
  const [isProductsModalOpen, setIsProductsModalOpen] = useState(false);
  const [modalProductsType, setModalProductsType] = useState(""); // "department", "outOfStock", "inactive", "lowStock"
  const [modalProductsTitle, setModalProductsTitle] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState(null);

  // Data
  const [rooms, setRooms] = useState([]);
  const [roomTypes, setRoomTypes] = useState([]);
  const [operationsData, setOperationsData] = useState({
    roomStats: {
      totalRooms: 0,
      availableRooms: 0,
      occupiedRooms: 0,
      reservedRooms: 0,
      outOfOrderRooms: 0,
      occupancyRate: 0,
      vacancyRate: 0
    },
    bookingSummary: {
      periodLabel: "Today",
      totalBookings: 0,
      totalRevenue: 0,
      avgDailyRate: 0
    }
  });

  const roomsCollectionRef = collection(db, "rooms");
  const roomTypesCollectionRef = collection(db, "roomTypes");
  const bookingsCollectionRef = collection(db, "bookings");
  const paymentsCollectionRef = collection(db, "payments");

  useEffect(() => {
    if (user && userData && ['Supreme', 'Admin', 'Cashier', 'Front Desk', 'Staff'].includes(userData.status)) {
      loadDashboardData();
    }
  }, [user, userData, selectedPeriod]);

  const getPeriodDates = (period) => {
    const now = new Date();
    let start, end = new Date();
    let label = "";

    switch (period) {
      case "today":
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate() + 1);
        label = "Today";
        break;
      case "yesterday":
      default:
        start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 1);
        end = new Date(now.getFullYear(), now.getMonth(), now.getDate());
        label = "Yesterday";
        break;
      case "thisWeek":
        const day = now.getDay();
        const diff = now.getDate() - day + (day === 0 ? -6 : 1);
        start = new Date(now.getFullYear(), now.getMonth(), diff);
        label = "This Week";
        break;
      case "lastMonth":
        start = new Date(now.getFullYear(), now.getMonth() - 1, 1);
        end = new Date(now.getFullYear(), now.getMonth(), 0);
        label = "Last Month";
        break;
      case "thisQuarter":
        const quarter = Math.floor(now.getMonth() / 3);
        start = new Date(now.getFullYear(), quarter * 3, 1);
        label = "This Quarter";
        break;
      case "thisYear":
        start = new Date(now.getFullYear(), 0, 1);
        label = "This Year";
        break;
      case "thisMonth":      
        start = new Date(now.getFullYear(), now.getMonth(), 1);
        label = "This Month";
        break;
    }

    return { start, end, label };
  };

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [roomsSnap, roomTypesSnap, bookingsSnap, paymentsSnap] = await Promise.all([
        getDocs(roomsCollectionRef),
        getDocs(roomTypesCollectionRef),
        getDocs(bookingsCollectionRef),
        getDocs(paymentsCollectionRef)
      ]);

      const roomsList = roomsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const roomTypesList = roomTypesSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const bookingsList = bookingsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      const paymentsList = paymentsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));

      setRooms(roomsList);
      setRoomTypes(roomTypesList.filter(rt => rt.active !== false));

      // Room Stats
      const totalRooms = roomsList.length;
      const availableRooms = roomsList.filter(r => r.status === 'Available').length;
      const occupiedRooms = roomsList.filter(r => r.status === 'Occupied').length;
      const outOfOrderRooms = roomsList.filter(r => r.status === 'Out-of-Order').length; 
      const reservedRooms   = roomsList.filter(r => r.status === 'Reserved').length;     
      const occupancyRate = totalRooms > 0 ? ((occupiedRooms / totalRooms) * 100).toFixed(1) : 0;
      const vacancyRate = totalRooms > 0 ? ((availableRooms / totalRooms) * 100).toFixed(1) : 0;

      // Booking Summary
      const { start, end, label } = getPeriodDates(selectedPeriod);
      const filteredBookings = bookingsList.filter(b => {
        const created = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(b.createdAt);
        return created >= start && (!end || created <= end);
      });

      const filteredPayments = paymentsList.filter(p => {
        const paidAt = p.timestamp?.toDate ? p.timestamp.toDate() : new Date(p.timestamp);
        return paidAt >= start && (!end || paidAt <= end);
      });

      const totalBookings = filteredBookings.length;
      const totalRevenue = filteredPayments.reduce((sum, p) => sum + (p.amount || 0), 0);
      const daysInPeriod = Math.ceil((end - start) / (1000 * 60 * 60 * 24)) || 1;
      const avgDailyRate = totalRevenue / daysInPeriod;

      setOperationsData({
        roomStats: {
          totalRooms,
          availableRooms,
          occupiedRooms,
          outOfOrderRooms,
          reservedRooms,
          occupancyRate,
          vacancyRate
        },
        bookingSummary: {
          periodLabel: label,
          totalBookings,
          totalRevenue,
          avgDailyRate: avgDailyRate.toFixed(0)
        }
      });

      const productsSnap = await getDocs(collection(db, "products"));
      const allProducts = productsSnap.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProducts(allProducts);

    } catch (err) {
      console.error("Error loading dashboard:", err);
      setError("Failed to load data");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  // Open modal for specific room type
  const openRoomTypeModal = (roomType) => {
    const roomsInThisType = rooms.filter(r => r.roomTypeId === roomType.id);
    setSelectedRoomType(roomType);
    setRoomsInType(roomsInThisType);
    setSelectedRooms([]);
    setNewStatus("Available");
    setIsRoomTypeModalOpen(true);
  };

  // Open general status modal (Available/Occupied/OOO)
  const openRoomModal = (category) => {
    setSelectedRoomCategory(category);
    setIsRoomModalOpen(true);
  };

  const toggleRoomSelection = (roomId) => {
    setSelectedRooms(prev =>
      prev.includes(roomId)
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId]
    );
  };

  const handleBulkStatusUpdate = async () => {
    if (selectedRooms.length === 0) {
      setError("Please select at least one room");
      setShowModal(true);
      return;
    }

    try {
      const updates = selectedRooms.map(roomId =>
        updateDoc(doc(db, "rooms", roomId), {
          status: newStatus,
          updatedAt: Timestamp.now()
        })
      );

      await Promise.all(updates);

      setSuccessMessage(`${selectedRooms.length} room(s) updated to ${newStatus}`);
      setShowModal(true);
      setSelectedRooms([]);
      setIsRoomTypeModalOpen(false);
      setIsRoomModalOpen(false);
      loadDashboardData();
    } catch (err) {
      console.error("Error updating rooms:", err);
      setError("Failed to update room status");
      setShowModal(true);
    }
  };

  const openReportModal = async () => {
    setIsReportModalOpen(true);
  };

  const handleGenerateReport = async () => {
    if (!reportStartDate || !reportEndDate) {
      setError("Please select both start and end dates");
      setShowModal(true);
      return;
    }

    const start = new Date(reportStartDate);
    start.setHours(0, 0, 0, 0);
    const end = new Date(reportEndDate);
    end.setHours(23, 59, 59, 999);

    if (start > end) {
      setError("Start date cannot be after end date");
      setShowModal(true);
      return;
    }

    const isDaily = reportStartDate === reportEndDate;
    await generateBookingReport(start, end, isDaily);
  };
  
  // Shared report generation logic
  const generateBookingReport = async (startDate, endDate, isDaily = false) => {
    try {
      setLoading(true);

      // ── 1. Get payments in the period ──
      const paymentsQuery = query(
        paymentsCollectionRef,
        where("timestamp", ">=", Timestamp.fromDate(startDate)),
        where("timestamp", "<=", Timestamp.fromDate(endDate))
      );
      const paymentsSnap = await getDocs(paymentsQuery);
      const relevantPaymentBookingIds = new Set(
        paymentsSnap.docs.map(doc => doc.data().bookingId).filter(Boolean)
      );

      // ── 2. Get all bookings ──
      const bookingsSnap = await getDocs(bookingsCollectionRef);
      const allBookings = bookingsSnap.docs.map(d => ({
        id: d.id,
        ...d.data()
      }));

      // ── 3. Rooms & room types maps ──
      const [roomsSnap, roomTypesSnap] = await Promise.all([
        getDocs(roomsCollectionRef),
        getDocs(roomTypesCollectionRef),
      ]);
      const roomsMap = new Map(roomsSnap.docs.map(d => [d.id, d.data()]));
      const roomTypesMap = new Map(roomTypesSnap.docs.map(d => [d.id, d.data()]));

      // ── 4. Filter logic ──
      const filteredBookings = allBookings.filter(booking => {
        const timestampsToCheck = [];

        // Creation
        if (booking.createdAt) {
          timestampsToCheck.push(booking.createdAt);
        }

        // Check-in actual
        if (booking.checkInActual || booking.checkedInAt) {
          timestampsToCheck.push(booking.checkInActual || booking.checkedInAt);
        }

        // Check-out actual
        if (booking.checkOutActual || booking.checkedOutAt) {
          timestampsToCheck.push(booking.checkOutActual || booking.checkedOutAt);
        }

        // Cancellation
        if (booking.cancelledAt) {
          timestampsToCheck.push(booking.cancelledAt);
        }

        // Any update (optional — can be noisy, comment out if too many false positives)
        // if (booking.updatedAt) {
        //   timestampsToCheck.push(booking.updatedAt);
        // }

        // Has payment in period?
        const hasPaymentInPeriod = relevantPaymentBookingIds.has(booking.id);

        // Convert all timestamps and check if ANY is in range
        const anyEventInRange = timestampsToCheck.some(ts => {
          const dt = ts?.toDate ? ts.toDate() : new Date(ts);
          return dt >= startDate && dt <= endDate;
        });

        return anyEventInRange || hasPaymentInPeriod;
      });

      if (filteredBookings.length === 0) {
        setError("No relevant booking activity found in the selected period");
        setShowModal(true);
        return;
      }

      const groups = {
        pending: filteredBookings.filter(b => b.status === 'pending'),
        checkedIn: filteredBookings.filter(b => b.status === 'checked-in'),
        checkedOut: filteredBookings.filter(b => b.status === 'checked-out'),
        cancelled: filteredBookings.filter(b => b.status === 'cancelled'),
        other: filteredBookings.filter(b => !['pending', 'checked-in', 'checked-out', 'cancelled'].includes(b.status || '')),
      };

      const doc = new jsPDF({ orientation: "landscape", unit: "mm", format: "a4" });
      let y = 10;

      // Header
      doc.addImage(Logo, "PNG", 14, y, 15, 15);
      doc.setFontSize(16);
      doc.setFont("helvetica", "bold");
      doc.text(HOTEL_NAME, 35, y + 5);
      doc.setFontSize(14);
      doc.setFont("helvetica", "normal");
      const title = isDaily
        ? `Daily Booking Report – ${startDate.toLocaleDateString("en-GB")}`
        : `Booking Report (${startDate.toLocaleDateString("en-GB")} – ${endDate.toLocaleDateString("en-GB")})`;
      doc.text(title, 35, y + 13);
      doc.setFontSize(10);
      const fullName = userData?.firstName && userData?.lastName
        ? `${userData.firstName} ${userData.lastName}`
        : "Staff";
      doc.text(`Generated by: ${fullName}`, 35, y + 18);
      doc.text(`Generated on: ${new Date().toLocaleString("en-GB")}`, 35, y + 23);
      y += 32;

      const addStatusSection = (title, bookings, showReason = false, showActivityInfo = false) => {
        if (bookings.length === 0) return;

        doc.setFontSize(13);
        doc.setFont("helvetica", "bold");
        doc.setTextColor(0);
        doc.text(title, 14, y);
        y += 8;

        const tableData = bookings.map(b => {
          const room = roomsMap.get(b.roomId) || {};
          const rtype = roomTypesMap.get(b.roomTypeId) || {};
          const checkIn = b.checkIn?.toDate?.() ? b.checkIn.toDate() : new Date(b.checkIn || 0);
          const checkOut = b.checkOut?.toDate?.() ? b.checkOut.toDate() : new Date(b.checkOut || 0);

          const total = Number(b.totalPrice || 0);
          const deposit = Number(b.deposit || 0);
          const balance = total - deposit;

          // Build incidental detail text and calculate incidental total
          let incidentalText = "None";
          let incidentalTotal = 0;
          const linked = b.linkedSales || [];
          if (linked.length > 0) {
            incidentalText = "";
            linked.forEach((sale, i) => {
              const saleTotal = Number(sale.grandTotal || 0);
              incidentalTotal += saleTotal;
              incidentalText += `Sale ${i + 1}: N${saleTotal.toLocaleString()}\n`;
              (sale.products || []).forEach(p => {
                incidentalText += `  • ${p.name} × ${p.quantity} = N${Number(p.total || 0).toLocaleString()}\n`;
              });
              incidentalText += "\n";
            });
            incidentalText = incidentalText.trim();
          }

          // Activity information - FIXED
          let activityInfo = "—";
          let activityTime = "—";
          
          if (showActivityInfo) {
            if (b.status === 'checked-in' && b.checkedInBy) {
              // Handle both object format and string format
              const checkedInBy = typeof b.checkedInBy === 'object' 
                ? (b.checkedInBy.name || 'Staff') 
                : (b.checkedInBy || 'Staff');
              const checkedInTime = b.checkedInTime?.toDate?.() 
                ? b.checkedInTime.toDate() 
                : (b.checkedInAt?.toDate?.() ? b.checkedInAt.toDate() : new Date(b.checkedInTime || b.checkedInAt || 0));
              
              activityInfo = checkedInBy;
              activityTime = checkedInTime.toLocaleString("en-GB");
            } else if (b.status === 'checked-out' && b.checkedOutBy) {
              // Handle both object format and string format
              const checkedOutBy = typeof b.checkedOutBy === 'object' 
                ? (b.checkedOutBy.name || 'Staff') 
                : (b.checkedOutBy || 'Staff');
              const checkedOutTime = b.checkedOutTime?.toDate?.() 
                ? b.checkedOutTime.toDate() 
                : (b.checkedOutAt?.toDate?.() ? b.checkedOutAt.toDate() : new Date(b.checkedOutTime || b.checkedOutAt || 0));
              
              activityInfo = checkedOutBy;
              activityTime = checkedOutTime.toLocaleString("en-GB");
            } else if (b.status === 'cancelled' && b.cancelledBy) {
              // Handle both object format and string format
              const cancelledBy = typeof b.cancelledBy === 'object' 
                ? (b.cancelledBy.name || 'Staff') 
                : (b.cancelledBy || 'Staff');
              const cancelledTime = b.cancelledTime?.toDate?.() 
                ? b.cancelledTime.toDate() 
                : (b.cancelledAt?.toDate?.() ? b.cancelledAt.toDate() : new Date(b.cancelledTime || b.cancelledAt || 0));
              
              activityInfo = cancelledBy;
              activityTime = cancelledTime.toLocaleString("en-GB");
            }
          }

          const row = [
            b.guestName || "—",
            room.roomNumber ? `${room.roomNumber}` : "—",
            rtype.name || "—",
            checkIn.toLocaleDateString("en-GB"),
            checkOut.toLocaleDateString("en-GB"),
            `N${total.toLocaleString()}`,
            `N${deposit.toLocaleString()}`,
            `N${balance.toLocaleString()}`,
            incidentalText
          ];

          if (showActivityInfo) {
            // Insert activity info (user and time) as separate columns
            row.splice(8, 0, activityInfo, activityTime);
          }

          if (showReason) {
            // Insert reason at the correct position (index 3, after Type)
            row.splice(3, 0, b.cancellationReason || "Not specified");
          }

          // Store incidental total for later calculation
          row._incidentalTotal = incidentalTotal;

          return row;
        });

        // Define columns based on what's being shown
        let head, colWidths;
        
        if (showActivityInfo) {
          // For checked-in, checked-out, cancelled - include activity info
          if (showReason) {
            head = ["Guest Name", "Room", "Type", "Reason", "Check-In", "Check-Out", "Total", "Deposit", "Balance", "Performed By", "Time", "Incidental"];
            colWidths = [30, 15, 20, 25, 18, 18, 20, 20, 20, 25, 25, 30];
          } else {
            head = ["Guest Name", "Room", "Type", "Check-In", "Check-Out", "Total", "Deposit", "Balance", "Performed By", "Time", "Incidental Charges"];
            colWidths = [35, 16, 22, 21, 21, 21, 21, 21, 25, 25, 35];
          }
        } else {
          // For pending and other - no activity info
          if (showReason) {
            head = ["Guest Name", "Room", "Type", "Reason", "Check-In", "Check-Out", "Total", "Deposit", "Balance", "Incidental"];
            colWidths = [37, 16, 22, 35, 24, 24, 24, 24, 24, 39];
          } else {
            head = ["Guest Name", "Room", "Type", "Check-In", "Check-Out", "Total", "Deposit", "Balance", "Incidental Charges"];
            colWidths = [44, 18, 25, 25, 25, 25, 25, 25, 56];
          }
        }

        // Calculate column start positions for proper alignment
        const columnPositions = [];
        let currentX = 14;
        colWidths.forEach((width, index) => {
          columnPositions[index] = currentX;
          currentX += width;
        });

        autoTable(doc, {
          startY: y,
          head: [head],
          body: tableData,
          theme: "striped",
          margin: { left: 14, right: 10 },
          styles: { fontSize: 8, cellPadding: 3, textColor: 0, overflow: 'linebreak', halign: 'left' },
          headStyles: { fillColor: [41, 128, 185], textColor: 255, fontSize: 9, halign: 'center' },
          columnStyles: colWidths.reduce((obj, w, i) => { obj[i] = { cellWidth: w }; return obj; }, {}),
        });

        y = doc.lastAutoTable.finalY + 6;

        // Totals row - properly aligned with columns
        const sectionTotal = bookings.reduce((s, b) => s + Number(b.totalPrice || 0), 0);
        const sectionDeposit = bookings.reduce((s, b) => s + Number(b.deposit || 0), 0);
        const sectionBalance = sectionTotal - sectionDeposit;
        const sectionIncidental = tableData.reduce((s, row) => s + (row._incidentalTotal || 0), 0);

        // Calculate total table width
        const totalTableWidth = colWidths.reduce((sum, width) => sum + width, 0);
        
        doc.setFillColor(220, 220, 220);
        doc.rect(14, y - 1, totalTableWidth, 7, "F"); // Background extends to end of table

        doc.setFont("helvetica", "bold");
        doc.setFontSize(9);
        doc.text("SECTION TOTALS", 16, y + 5);

        // Find column indices
        const totalIndex = head.indexOf("Total");
        const depositIndex = head.indexOf("Deposit");
        const balanceIndex = head.indexOf("Balance");
        const incidentalIndex = head.indexOf("Incidental") !== -1 ? head.indexOf("Incidental") : head.indexOf("Incidental Charges");

        // Calculate the X position for each total - positioned under the center of each column
        const totalX = columnPositions[totalIndex] + (colWidths[totalIndex] / 2);
        const depositX = columnPositions[depositIndex] + (colWidths[depositIndex] / 2);
        const balanceX = columnPositions[balanceIndex] + (colWidths[balanceIndex] / 2);
        const incidentalX = columnPositions[incidentalIndex] + (colWidths[incidentalIndex] / 2);

        // Align totals under their respective columns
        doc.text(`N${sectionTotal.toLocaleString()}`, totalX, y + 5, { align: "center" });
        doc.text(`N${sectionDeposit.toLocaleString()}`, depositX, y + 5, { align: "center" });
        doc.text(`N${sectionBalance.toLocaleString()}`, balanceX, y + 5, { align: "center" });
        doc.text(`N${sectionIncidental.toLocaleString()}`, incidentalX, y + 5, { align: "center" });

        y += 14;

        if (y > 260) {
          doc.addPage();
          y = 20;
        }
      };

      addStatusSection("Pending Bookings", groups.pending);
      addStatusSection("Checked-in Bookings", groups.checkedIn, false, true); // Show activity info
      addStatusSection("Checked-out Bookings", groups.checkedOut, false, true); // Show activity info
      addStatusSection("Cancelled Bookings", groups.cancelled, true, true); // Show reason AND activity info

      if (groups.other.length > 0) {
        addStatusSection("Other Bookings", groups.other);
      }

      // Final grand totals - including incidental
      y += 10;
      doc.setFontSize(12);
      doc.setFont("helvetica", "bold");
      doc.text("GRAND TOTALS (ALL BOOKINGS)", 14, y);
      y += 8;

      const gTotal = filteredBookings.reduce((s, b) => s + Number(b.totalPrice || 0), 0);
      const gDeposit = filteredBookings.reduce((s, b) => s + Number(b.deposit || 0), 0);
      const gBalance = gTotal - gDeposit;
      
      // Calculate grand incidental total
      const gIncidental = filteredBookings.reduce((s, b) => {
        let incidental = 0;
        const linked = b.linkedSales || [];
        linked.forEach(sale => {
          incidental += Number(sale.grandTotal || 0);
        });
        return s + incidental;
      }, 0);

      doc.setFontSize(11);
      doc.text(`Total Room Charges:    N${gTotal.toLocaleString()}`, 18, y); y += 7;
      doc.text(`Total Deposits:              N${gDeposit.toLocaleString()}`, 18, y); y += 7;
      doc.text(`Total Outstanding:        N${gBalance.toLocaleString()}`, 18, y); y += 7;
      doc.text(`Total Incidental Sales:  N${gIncidental.toLocaleString()}`, 18, y); y += 7;
      doc.text(`Grand Total:                  N${(gTotal + gIncidental).toLocaleString()}`, 18, y);

      // Page numbers
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFontSize(9);
        doc.text(`Page ${i} of ${pageCount}`, doc.internal.pageSize.width - 40, doc.internal.pageSize.height - 10);
      }

      const filename = isDaily
        ? `daily_booking_report_${startDate.toISOString().split('T')[0]}.pdf`
        : `booking_report_${startDate.toISOString().split('T')[0]}_to_${endDate.toISOString().split('T')[0]}.pdf`;

      doc.save(filename);
      setIsReportModalOpen(false);

    } catch (err) {
      console.error("Report error:", err);
      setError("Failed to generate report");
      setShowModal(true);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => `₦${Number(amount || 0).toLocaleString()}`;

  if (loading) return <Loader text="Loading Dashboard..." />;

  if (!userData || !['Supreme', 'Admin', 'Front Desk', 'Cashier', 'Staff'].includes(userData.status)) {
    return (
      <div className="dashboard-container padding-bottom-d">
        <div style={{ textAlign: 'center', paddingTop: '100px' }}>
          <i className="fas fa-lock fa-4x" style={{ color: '#e74c3c' }}></i>
          <h2>Access Restricted - Admin Only</h2>
          <Link to="/home" className="login-button">Back to Home</Link>
        </div>
      </div>
    );
  }

  const getFilteredRooms = () => {
    switch (selectedRoomCategory) {
      case 'available': return rooms.filter(r => r.status === 'Available');
      case 'occupied': return rooms.filter(r => r.status === 'Occupied');
      case 'reserved': return rooms.filter(r => r.status === 'Reserved');
      case 'out-of-order': return rooms.filter(r => r.status === 'Out-of-Order');
      default: return [];
    }
  };

  // Count rooms per type and status
  const getRoomTypeStats = (roomTypeId) => {
    const typeRooms = rooms.filter(r => r.roomTypeId === roomTypeId);
    const total = typeRooms.length;
    const available = typeRooms.filter(r => r.status === 'Available').length;
    const occupied = typeRooms.filter(r => r.status === 'Occupied').length;
    const reserved = typeRooms.filter(r => r.status === 'Reserved').length;
    const ooo = typeRooms.filter(r => r.status === 'Out-of-Order').length;
    return { total, available, occupied, reserved, ooo };
  };
  const getProductStats = () => {
    // Filter products based on user's department
    let filteredProducts = products;
    
    // If user has a department, filter products to only show those in same department
    if (userData?.department) {
      filteredProducts = products.filter(p => p.department === userData.department);
    }

    // Use filteredProducts instead of all products
    const active = filteredProducts.filter(p => p.isActive === true);
    const inactive = filteredProducts.filter(p => p.isActive === false);
    const outOfStock = filteredProducts.filter(p => p.isActive && (p.quantity ?? 0) === 0);
    const lowStock = filteredProducts.filter(p => {
      if (!p.isActive || (p.quantity ?? 0) <= 0) return false;
      return p.minStockLevel != null && (p.quantity ?? 0) <= p.minStockLevel;
    });

    // Department breakdown (only active products)
    // If user has department, show only their department stats
    const departmentsMap = {};
    
    if (userData?.department) {
      // Show only the user's department
      const dept = userData.department;
      const deptProducts = active.filter(p => p.department === dept);
      if (deptProducts.length > 0) {
        const totalQty = deptProducts.reduce((sum, p) => sum + (p.quantity ?? 0), 0);
        departmentsMap[dept] = { count: deptProducts.length, totalQty };
      }
    } else {
      // Admin/No department: show all departments
      active.forEach(p => {
        const dept = p.department || "No Department";
        if (!departmentsMap[dept]) {
          departmentsMap[dept] = { count: 0, totalQty: 0 };
        }
        departmentsMap[dept].count++;
        departmentsMap[dept].totalQty += (p.quantity ?? 0);
      });
    }

    return {
      totalProducts: filteredProducts.length,
      activeProducts: active.length,
      inactiveProducts: inactive.length,
      outOfStock: outOfStock.length,
      lowStock: lowStock.length,
      departments: Object.entries(departmentsMap).map(([name, stats]) => ({
        name,
        ...stats
      }))
    };
  };

  const getProductsForModal = (type, deptName = null) => {
    let filtered = [];

    // Start with all products
    let productsToFilter = products;
    
    // If user has department, filter by department first
    if (userData?.department) {
      productsToFilter = products.filter(p => p.department === userData.department);
    }

    switch (type) {
      case "outOfStock":
        filtered = productsToFilter.filter(p => p.isActive && (p.quantity ?? 0) === 0);
        break;
      case "inactive":
        filtered = productsToFilter.filter(p => p.isActive === false);
        break;
      case "lowStock":
        filtered = productsToFilter.filter(p => {
          if (!p.isActive || (p.quantity ?? 0) <= 0) return false;
          return p.minStockLevel != null && (p.quantity ?? 0) <= p.minStockLevel;
        });
        break;
      case "department":
        if (deptName) {
          filtered = productsToFilter.filter(p => p.department === deptName && p.isActive);
        } else if (userData?.department) {
          // If user has department, show their department's products
          filtered = productsToFilter.filter(p => p.department === userData.department && p.isActive);
        } else {
          filtered = productsToFilter.filter(p => p.isActive);
        }
        break;
      default:
        filtered = [];
    }

    // Sort by name
    return filtered.sort((a, b) => (a.name || "").localeCompare(b.name || ""));
  };

  return (
    <div className="dashboard-container padding-bottom-d">
      <div className="pos-dashboard" style={{ maxWidth: '1400px', margin: 'auto' }}>  
        <p className="caps login-heading page-heading page-heading-d">ROOMS OVERVIEW</p>
          <div className="stats-grid stats-grid-desktop">
            <div className="stat-content" onClick={() => openRoomModal('available')}>
              <i className="fas fa-door-open" style={{ fontSize: '3rem', color: '#9B59B6' }}></i>
              <p className="stat-value"><span className="bold italics size2">{operationsData.roomStats.availableRooms}</span><br />Available Rooms</p>
            </div>
            <div className="stat-content" onClick={() => openRoomModal('occupied')}>
              <i className="fas fa-bed" style={{ fontSize: '3rem', color: '#5C9E7D' }}></i>
              <p className="stat-value"><span className="bold italics size2">{operationsData.roomStats.occupiedRooms}</span><br />Occupied Rooms</p>
            </div>
            <div className="stat-content" onClick={() => openRoomModal('reserved')}>
              <i className="fas fa-calendar-check" style={{ fontSize: '3rem', color: '#caac32' }}></i>
              <p className="stat-value">
                <span className="bold italics size2">{operationsData.roomStats.reservedRooms}</span><br />
                Reserved Rooms
              </p>
            </div>
            <div className="stat-content" onClick={() => openRoomModal('out-of-order')}>
              <i className="fas fa-tools" style={{ fontSize: '3rem', color: '#E56B6F' }}></i>
              <p className="stat-value"><span className="bold italics size2">{operationsData.roomStats.outOfOrderRooms}</span><br />Out of Order</p>
            </div>
            <div className="stat-content" onClick={userData.status === 'Staff' ? undefined : () => navigate('/rooms')}>
              <i className="fa-solid fa-hotel" style={{ fontSize: '3rem', color: '#e9ec30' }}></i>
              <p className="stat-value"><span className="bold italics size2">{operationsData.roomStats.totalRooms}</span><br />Total Rooms</p>
            </div>
          </div>        

        {/* Room Types Breakdown */}
        <div style={{ marginBottom: '20px' }}>
          <p className="caps login-heading page-heading page-heading-d">ROOM TYPES</p>
          <div className="stats-grid stats-grid-desktop">
            {roomTypes.length === 0 ? (
              <p>No room types defined yet.</p>
            ) : (
              roomTypes.map(type => {
                const stats = getRoomTypeStats(type.id);
                return (                  
                  <div className="stat-content" key={type.id} onClick={() => openRoomTypeModal(type)}>
                    <i className="fa-solid fa-door-closed" style={{ fontSize: '3rem', color: '#00CED1' }}></i>
                    <p className="stat-value"><span className="bold italics size4">{stats.total}</span><br />{type.name}</p>
                    <p>
                      <span className="size5 italics font-light-gray"><strong>{stats.available}</strong> Available</span><br />
                      <span className="size5 italics font-light-gray"><strong>{stats.occupied}</strong> Occupied</span><br />
                      <span className="size5 italics font-light-gray"><strong>{stats.reserved}</strong> Reserved</span><br />
                      <span className="size5 italics font-light-gray"><strong>{stats.ooo}</strong> Out of Order</span><br />
                    </p>                    
                  </div>                                    
                );
              })
            )}
          </div>
        </div>

        {/* Booking Summary */}
        {(userData.status === "Admin" || userData.status === "Supreme") && (
          <>
            <div>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="input-field1"
                style={{ padding: '12px', fontSize: '16px' }}
              >
                <option value="today">Today</option>
                <option value="yesterday">Yesterday</option>
                <option value="thisWeek">This Week</option>
                <option value="thisMonth">This Month</option>
                <option value="lastMonth">Last Month</option>
                <option value="thisQuarter">This Quarter</option>
                <option value="thisYear">This Year</option>
              </select>
            </div>

            <p className="caps login-heading page-heading page-heading-d">Booking Summary — {operationsData.bookingSummary.periodLabel}</p>          
            <div className="stats-grid stats-grid-desktop">
              <div className="stat-content">
                <i className="fas fa-calendar-check" style={{ fontSize: '3rem', color: '#c2db34' }}></i>
                <p className="stat-value">
                  <span className="bold italics size4">{operationsData.bookingSummary.totalBookings}</span><br />Total Bookings
                </p>
              </div>
              <div className="stat-content">
                <i className="fas fa-money-bill" style={{ fontSize: '3rem', color: '#cc2eaa' }}></i>
                <p className="stat-value">
                  <span className="bold italics size4">{formatCurrency(operationsData.bookingSummary.totalRevenue)}</span><br />Total Revenue
                </p>
              </div>
              <div className="stat-content">
                <i className="fas fa-chart-line" style={{ fontSize: '3rem', color: '#F39C12' }}></i>
                <p className="stat-value">
                  <span className="bold italics size4">{formatCurrency(operationsData.bookingSummary.avgDailyRate)}</span><br />Avg. Daily Rate
                </p>
              </div>
              <div className="stat-content" style={{ cursor: 'pointer' }} onClick={openReportModal}>
                <i className="fas fa-file-alt" style={{ fontSize: '3rem', color: '#3c78e7' }}></i>
                <p className="stat-value">
                  <span className="bold italics size2">Generate</span><br />Booking Report
                </p>
              </div>
            </div>
          </>
        )}

        {/* ── Products Overview ── */}
        <p className="caps login-heading page-heading page-heading-d" style={{ marginTop: '40px' }}>
          PRODUCTS OVERVIEW
        </p>

        {(() => {
          const stats = getProductStats();

          return (
            <div className="stats-grid stats-grid-desktop">              
              {/* Products by Department – only show if user doesn't have department OR is admin */}
              {!userData?.department || ['Supreme', 'Admin'].includes(userData.status) ? (
                <div 
                  className="stat-content" 
                  onClick={() => {
                    setModalProductsType("department");
                    setModalProductsTitle("Products by Department");
                    setSelectedDepartment(null);
                    setIsProductsModalOpen(true);
                  }}
                >
                  <i className="fa-solid fa-layer-group" style={{ fontSize: '3rem', color: '#3A7CA5' }}></i>
                  <p className="stat-value">
                    <span className="bold italics size2">{stats.departments.length}</span><br />
                    Departments
                  </p>
                </div>
              ) : (
                // If user has department, show their department info instead
                <div 
                  className="stat-content" 
                  onClick={() => {
                    setModalProductsType("department");
                    setModalProductsTitle(`Products in ${userData.department}`);
                    setSelectedDepartment(userData.department);
                    setIsProductsModalOpen(true);
                  }}
                >
                  <i className="fa-solid fa-layer-group" style={{ fontSize: '3rem', color: '#3A7CA5' }}></i>
                  <p className="stat-value">
                    <span className="bold italics size2">
                      {stats.departments.find(d => d.name === userData.department)?.count || 0}
                    </span><br />
                    {userData.department}
                  </p>
                </div>
              )}

              {/* Out of Stock */}
              <div 
                className="stat-content" 
                onClick={() => {
                  setModalProductsType("outOfStock");
                  setModalProductsTitle("Out of Stock Products");
                  setIsProductsModalOpen(true);
                }}
              >
                <i className="fa-solid fa-box-archive" style={{ fontSize: '3rem', color: '#E74C3C' }}></i>
                <p className="stat-value">
                  <span className="bold italics size2">{stats.outOfStock}</span><br />
                  Out of Stock
                </p>
              </div>

              {/* Inactive Products */}
              <div 
                className="stat-content" 
                onClick={() => {
                  setModalProductsType("inactive");
                  setModalProductsTitle("Inactive Products");
                  setIsProductsModalOpen(true);
                }}
              >
                <i className="fa-solid fa-ban" style={{ fontSize: '3rem', color: '#95A5A6' }}></i>
                <p className="stat-value">
                  <span className="bold italics size2">{stats.inactiveProducts}</span><br />
                  Inactive
                </p>
              </div>

              {/* Low Stock */}
              <div 
                className="stat-content" 
                onClick={() => {
                  setModalProductsType("lowStock");
                  setModalProductsTitle("Low Stock Products");
                  setIsProductsModalOpen(true);
                }}
              >
                <i className="fa-solid fa-exclamation-triangle" style={{ fontSize: '3rem', color: '#F39C12' }}></i>
                <p className="stat-value">
                  <span className="bold italics size2">{stats.lowStock}</span><br />
                  Low Stock
                </p>
              </div>

              {/* Optional: Total Products */}
              <div className="stat-content">
                <i className="fa-solid fa-boxes-stacked" style={{ fontSize: '3rem', color: '#2ECC71' }}></i>
                <p className="stat-value">
                  <span className="bold italics size2">{stats.activeProducts}</span><br />
                  Active Products
                </p>
              </div>
            </div>
          );
        })()}
      </div>

      {/* Room Type Detail Modal */}
      <Modal
        isOpen={isRoomTypeModalOpen}
        onRequestClose={() => {
          setIsRoomTypeModalOpen(false);
          setSelectedRooms([]);
        }}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">
            {selectedRoomType?.name} Rooms ({roomsInType.length} total)
          </h2>
        </div>
        <div className="modal-body">
          {(userData.status !== "Staff") && (
          <div style={{ marginBottom: '20px' }}>
            <label><strong>Change selected rooms to:</strong></label>
            <select
              value={newStatus}
              onChange={(e) => setNewStatus(e.target.value)}
              className="input-field"
              style={{ width: '100%', marginTop: '10px' }}
            >
              <option value="Available">Available</option>
              <option value="Reserved">Reserved</option>
              <option value="Out-of-Order">Out of Order</option>
            </select>
          </div>
          )}

          <div className="scroll-fixed extra-amenities" style={{maxHeight: '300px'}}>
            {roomsInType.length === 0 ? (
              <p>No rooms in this type.</p>
            ) : (
              roomsInType.map(room => (
                <div
                  key={room.id}
                  style={{
                    padding: '5px',
                    background: selectedRooms.includes(room.id) ? '#464646' : '',
                    borderBottom: '1px solid #464646',
                    borderRadius: '4px',
                    marginBottom: '10px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '15px'
                  }}
                >
                  <label className="room-item" style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    {(userData.status !== "Staff") && (
                    <input
                      type="checkbox"
                      className='gold-tick'
                      checked={selectedRooms.includes(room.id)}
                      onChange={() => toggleRoomSelection(room.id)}
                    />
                    )}
                    <div>
                      <strong>Room {room.roomNumber}</strong><br />
                      <small>
                        <strong 
                          className={
                            room.status === 'Available' 
                              ? 'font-green' 
                              : room.status === 'Occupied' 
                                ? 'font-gold' 
                                : room.status === 'Reserved' 
                                  ? 'font-yellow' 
                                  : 'font-orange'
                          }
                        >
                          {room.status}
                        </strong>
                      </small>
                    </div>
                   </label> 
                </div>
              ))
            )}
          </div>
          
          <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
            <button className="login-button" onClick={() => {
              setIsRoomTypeModalOpen(false);
              setSelectedRooms([]);
            }}>
              Cancel
            </button>
            {(userData.status !== "Staff") && (
              <>
                <button
                  className="login-button"
                  style={{ backgroundColor: '#27ae60' }}
                  onClick={handleBulkStatusUpdate}
                  disabled={selectedRooms.length === 0}
                >
                  Update {selectedRooms.length} Room(s)
                </button>
              </>
            )}
          </div>          
        </div>
      </Modal>

      {/* General Status Modal (Available/Occupied/OOO) */}
      <Modal
        isOpen={isRoomModalOpen}
        onRequestClose={() => {
          setIsRoomModalOpen(false);
          setSelectedRooms([]);
        }}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">
            {userData.status !== "Staff" && selectedRoomCategory !== 'occupied' && "Manage "} {selectedRoomCategory.replace('-', ' ')} Rooms {(selectedRoomCategory === 'occupied' && `(View Only)`)}            
          </h2>
        </div>

        <div className="modal-body">
          {/* Only show status change controls when NOT viewing occupied rooms */}
          {selectedRoomCategory !== 'occupied' && userData.status !== "Staff" && (
            <div style={{ marginBottom: '20px' }}>
              <label><strong>Change selected rooms to:</strong></label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="input-field"
                style={{ width: '100%', marginTop: '10px' }}
              >
                <option value="Available">Available</option>
                <option value="Reserved">Reserved</option>
                <option value="Out-of-Order">Out of Order</option>
              </select>
            </div>
          )}

          <div className="scroll-fixed extra-amenities" style={{ maxHeight: '300px' }}>
            {getFilteredRooms().length === 0 ? (
              <p>No rooms in this category</p>
            ) : (
              getFilteredRooms().map(room => {
                const isOccupiedView = selectedRoomCategory === 'occupied';

                return (
                  <div
                    key={room.id}
                    style={{
                      padding: '8px 12px',
                      borderBottom: '1px solid #444',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '12px',
                      background: selectedRooms.includes(room.id) && !isOccupiedView ? '#464646' : 'transparent',
                      borderRadius: '4px',
                      marginBottom: '6px'
                    }}
                  >
                    <label className="room-item" style={{ cursor: 'pointer', display: 'flex', alignItems: 'flex-start', gap: '10px' }}>
                    {/* Checkbox – only shown when NOT occupied view AND not Staff */}
                      {!isOccupiedView && userData.status !== "Staff" && (
                        <input
                          type="checkbox"
                          className='gold-tick'
                          checked={selectedRooms.includes(room.id)}
                          onChange={() => toggleRoomSelection(room.id)}
                        />
                      )}

                      <div style={{ flex: 1 }}>
                        <strong>Room {room.roomNumber}</strong> 
                        {room.roomTypeName && ` - ${room.roomTypeName}`}
                        <br />
                        <small>
                          <strong
                            className={
                              room.status === 'Available' ? 'font-green' :
                              room.status === 'Occupied'  ? 'font-gold' :
                              room.status === 'Reserved'  ? 'font-yellow' :
                              'font-orange'
                            }
                          >
                            {room.status}
                          </strong>
                        </small>
                      </div>
                    </label>

                    {/* Optional: show more info only in view mode */}
                    {isOccupiedView && (
                      <small style={{ color: '#aaa' }}>
                        {room.guestName ? `(${room.guestName})` : ''}
                      </small>
                    )}
                  </div>
                );
              })
            )}
          </div>

          <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
            <button className="login-button" onClick={() => {
              setIsRoomModalOpen(false);
              setSelectedRooms([]);
            }}>
              Close
            </button>

            {/* Update button – only shown when NOT occupied view AND not Staff */}
            {selectedRoomCategory !== 'occupied' && userData.status !== "Staff" && (
              <button
                className="login-button"
                style={{ backgroundColor: '#27ae60' }}
                onClick={handleBulkStatusUpdate}
                disabled={selectedRooms.length === 0}
              >
                Update {selectedRooms.length} Room(s)
              </button>
            )}
          </div>
        </div>
      </Modal>

      {/* Single Report Date Selection Modal */}
      <Modal
        isOpen={isReportModalOpen}
        onRequestClose={() => {
          setIsReportModalOpen(false);
          setReportStartDate("");
          setReportEndDate("");
        }}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">Generate Booking Report</h2>
          <p style={{ color: '#aaa', fontSize: '14px', marginTop: '5px' }}>
            Select date range. For daily report, use the same start and end date.
          </p>
        </div>
        <div className="modal-body">
          <div style={{ marginBottom: '20px' }}>
            <label className="input-label">Start Date</label>
            <input
              type="date"
              value={reportStartDate}
              onChange={(e) => setReportStartDate(e.target.value)}
              className="input-field"
              style={{ width: '100%', marginTop: '8px' }}
            />
          </div>
          <div style={{ marginBottom: '20px' }}>
            <label className="input-label">End Date</label>
            <input
              type="date"
              value={reportEndDate}
              onChange={(e) => setReportEndDate(e.target.value)}
              className="input-field"
              style={{ width: '100%', marginTop: '8px' }}
            />
          </div>

          {reportStartDate && reportEndDate && (
            <div style={{
              padding: '10px',
              marginBottom: '20px',
              backgroundColor: reportStartDate === reportEndDate ? '#2a2a2a' : '#2a2a2a',
              borderRadius: '6px',
              border: '1px solid #444'
            }}>
              <small style={{ color: '#aaa' }}>
                <strong>Report Type:</strong> {reportStartDate === reportEndDate ? 'Daily Report' : 'Range Report'}<br />
                <strong>Period:</strong> {new Date(reportStartDate).toLocaleDateString("en-GB")} 
                {reportStartDate !== reportEndDate ? ` to ${new Date(reportEndDate).toLocaleDateString("en-GB")}` : ''}
              </small>
            </div>
          )}

          <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'flex-end', gap: '15px' }}>
            <button className="login-button" onClick={() => {
              setIsReportModalOpen(false);
              setReportStartDate("");
              setReportEndDate("");
            }}>
              Cancel
            </button>
            <button
              className="login-button"
              onClick={handleGenerateReport}
              disabled={!reportStartDate || !reportEndDate}
            >
              Generate PDF
            </button>
          </div>
        </div>
      </Modal>

      {/* Products Detail Modal */}
      <Modal
        isOpen={isProductsModalOpen}
        onRequestClose={() => {
          setIsProductsModalOpen(false);
          setSelectedDepartment(null);
        }}
        className="custom-modal"
        overlayClassName="custom-modal-overlay"
      >
        <div className="modal-header">
          <h2 className="size5 caps bold">{modalProductsTitle}</h2>
        </div>

        <div className="modal-body">
          {/* If viewing departments list */}
          {modalProductsType === "department" && !selectedDepartment ? (
            <div className="scroll-fixed extra-amenities" style={{ maxHeight: '400px' }}>
              {getProductStats().departments.length === 0 ? (
                <p>No departments found.</p>
              ) : (
                getProductStats().departments.map((dept, idx) => (
                  <div
                    key={dept.name}
                    className="room-item"
                    style={{
                      padding: '12px',
                      marginBottom: '8px',
                      background: '#2a2a2a',
                      borderRadius: '6px',
                      cursor: 'pointer'
                    }}
                    onClick={() => {
                      setSelectedDepartment(dept.name);
                    }}
                  >
                    <strong>{dept.name}</strong>
                    <div style={{ marginTop: '6px', color: '#aaa' }}>
                      {dept.count} products • {dept.totalQty.toLocaleString()} items in stock
                    </div>
                  </div>
                ))
              )}
            </div>
          ) : (
            /* Product list view */
            <div className="scroll-fixed extra-amenities" style={{ maxHeight: '400px' }}>
              {(() => {
                const list = getProductsForModal(
                  modalProductsType,
                  selectedDepartment
                );

                if (list.length === 0) {
                  return <p style={{ textAlign: 'center', padding: '30px 0' }}>No products in this category</p>;
                }

                return list.map((product) => (
                  <div
                    key={product.id}
                    style={{
                      padding: '10px 12px',
                      borderBottom: '1px solid #444',
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'center'
                    }}
                  >
                    <div>
                      <strong>{product.name}</strong>
                      <br />
                      <small style={{ color: '#aaa' }}>
                        {product.department && (
                          <>Dept: {product.department} | </>
                        )}
                        ₦{(product.price || 0).toLocaleString()}
                      </small>
                    </div>
                    <div style={{
                      fontWeight: 'bold',
                      color: product.quantity <= 0 ? '#e74c3c' :
                            product.quantity <= (product.minStockLevel || 9999) ? '#f39c12' : '#2ecc71'
                    }}>
                      {product.quantity ?? 0}
                    </div>
                  </div>
                ));
              })()}
            </div>
          )}
        </div>

        <div style={{ marginTop: '20px', textAlign: 'right' }}>
          <button
            className="login-button"
            onClick={() => {
              setIsProductsModalOpen(false);
              setSelectedDepartment(null);
            }}
          >
            Close
          </button>
        </div>
      </Modal>

      {/* Success/Error Modal */}
      {showModal && (
        <SuccessModal
          icon={error ? "xmark" : "check"}
          iconColor={error ? "red" : "green"}
          message={error || successMessage}
          onClose={() => {
            setError("");
            setSuccessMessage("");
            setShowModal(false);
          }}
        />
      )}    
    </div>
  );
};

export default HotelOperationsDashboard;